// GalleryTable.tsx
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Button,
  Box,
  Typography,
  Modal,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  IGalleryResource,
  useCreateGalleryResourceMutation,
  GalleryResourceInput,
  CreateGalleryResourceMutation,
  MinistryByIdQuery,
  Exact,
  Scalars,
  useUpdateGalleryResourceMutation,
  UpdateGalleryResourceMutation,
  useDeleteGalleryResourceMutation,
  DeleteGalleryResourceMutation,
} from "../../graphql/generated/graphql";
import { ResourceImage } from "../resources/SourceImage";
import { GalleryResourceForm } from "./GalleryModal";
import { initTempGalleryResourceInput } from "../resources/const";
import { getGalleryResourceInput } from "../resources/util";
import { ApolloQueryResult, FetchResult } from "@apollo/client";
import { DeleteModal } from "../../libs/ui/DeleteModal";
import { NormalParagraphHTMLContent } from "../common/NormalParagraph";

export default function GalleryTable({
  galleries,
  ministryId,
  refetch,
}: {
  galleries: IGalleryResource[];
  ministryId?: string;
  refetch: (variables?: Partial<Exact<{
    ministryId: Scalars["Float"]["input"];
}>> | undefined) => Promise<ApolloQueryResult<MinistryByIdQuery>>
}) {
  const [openFormModal, setOpenFormModal] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [galleryResourceId, setGalleryResourceId] = useState<number | undefined>(undefined);
  const [onEdit, setOnEdit] = useState(false);
  const [tempGalleryResourceInput, setTempGalleryResourceInput] =
    useState<IGalleryResource>(initTempGalleryResourceInput);

  const [createGalleryResourceMutation] = useCreateGalleryResourceMutation();
  const [updateGalleryResourceMutation] = useUpdateGalleryResourceMutation();
  const [deleteGalleryResourceMutation] = useDeleteGalleryResourceMutation();

  const onAddGalleyResources = () => {
    setOpenFormModal(true);
  };

  const onEditGalleryResource = (row: IGalleryResource) => {
    setTempGalleryResourceInput(getGalleryResourceInput(row));
    setOnEdit(true);
    setOpenFormModal(true);
  };

  const updateGalleryResource = async (data: GalleryResourceInput) => {
    const { data: createResult }: FetchResult<UpdateGalleryResourceMutation> =
      await updateGalleryResourceMutation({
        variables: { data },
      });

    if (createResult?.UpdateGalleryResource.data) {
      await refetch({ministryId: +ministryId!});
    }
  };
  const createGalleryResource = async (data: GalleryResourceInput) => {
    const { data: createResult }: FetchResult<CreateGalleryResourceMutation> =
      await createGalleryResourceMutation({
        variables: { galleryResource: data },
      });

    if (createResult?.CreateGalleryResource.data) {
      await refetch({ministryId: +ministryId!});
    }
  };

  const handleSave = async (data: GalleryResourceInput) => {
    if (onEdit) {
      if (ministryId)
        await updateGalleryResource({
          ...data,
          id: tempGalleryResourceInput.id,
          ministryId: +ministryId,
        });
    } else {
      if (ministryId)
        await createGalleryResource({ ...data, ministryId: +ministryId });
    }
    setOnEdit(false);
    setOpenFormModal(false);
    setTempGalleryResourceInput(initTempGalleryResourceInput);
  };

  const onDelete = (id: number) => {
    setGalleryResourceId(id)
    setDeleteModalVisible(true)
  }

  const handleDelete = async () => {
    const { data: deleteResult }: FetchResult<DeleteGalleryResourceMutation> =
      await deleteGalleryResourceMutation({
        variables: { id: galleryResourceId! },
      });

    if (deleteResult?.DeleteGalleryResource.message === 'success') {
      await refetch({ministryId: +ministryId!});
      setDeleteModalVisible(false)
    }
    
  };

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Gallery Resources</Typography>
        <Button
          onClick={() => onAddGalleyResources()}
          variant="contained"
          color="primary"
        >
          Add Gallery Resource
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow >
              <TableCell sx={{ width: "80px", padding: "4px", fontWeight: 'bold' }}>
                Actions
              </TableCell>
              <TableCell sx={{ width: "100px", padding: "4px", fontWeight: 'bold' }}>Sorting Order</TableCell>
              <TableCell sx={{ width: "110px", padding: "4px", fontWeight: 'bold' }}>
                Image
              </TableCell>
              <TableCell sx={{
                fontWeight: 'bold'
              }}>Title</TableCell>
              <TableCell sx={{
                fontWeight: 'bold'
              }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {galleries.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ width: "120px", padding: "4px" }}>
                  <IconButton
                    onClick={() => onEditGalleryResource(row)}
                    aria-label="edit"
                    size='medium'
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton aria-label="delete" size="small" onClick={()=> onDelete(row.id!)}>
                    <DeleteIcon fontSize='medium' />
                  </IconButton>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    variant="outlined"
                    label={row.sortingOrder}
                    size="small"
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      backgroundColor: (theme) => theme.palette.primary.main + '22',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: "100px", padding: "4px" }}>
                  <ResourceImage url={row.imageUrl!} altString={row.title!} />
                </TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell><NormalParagraphHTMLContent label={row.description ?? ''} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal
        open={openFormModal}
        onClose={() => {
          setOpenFormModal(false);
          setTempGalleryResourceInput(initTempGalleryResourceInput);
          setOnEdit(false);
        }}
      >
        <Box>
          <GalleryResourceForm
            onEdit={onEdit}
            value={tempGalleryResourceInput}
            setValue={setTempGalleryResourceInput}
            onCancel={() => {
              setOnEdit(false);
              setTempGalleryResourceInput(initTempGalleryResourceInput);
              setOpenFormModal(false);
            }}
            onSave={handleSave}
          />
        </Box>
      </Modal>
      <DeleteModal
        title="Are you sure you want to delete this gallery resource?"
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setGalleryResourceId(undefined);
        }}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
